import React, { useEffect, useState } from "react";
import { Select, Button } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import { DropDown } from "../../assets/icons";
import ListHeader from "../../molecules/ListHeader/ListHeader";
import ButtonLoader from "../../atoms/buttonLoader";
import {
  AllocateProject,
  getProjectsData,
  getTasksData,
  getWorkersData,
} from "../../services/service";
import { CustomToast } from "../../atoms/toastMessage";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

const WorkerProjectAllocation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [projectData, setProjectData] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [workers, setWorkers] = useState([]);
  const itemsPerPage = 100;
  const navigate = useNavigate();

  const handleGetData = async (limit, page) => {
    setLoader(true);
    try {
      const response = await getProjectsData(page, limit);
      if (response?.success) {
        setProjectData(response?.data?.projects || []);
      }
    } catch (error) {
      console.error("Error fetching projects data:", error);
    } finally {
      setLoader(false);
    }
  };

  const handleGetTasks = async () => {
    setLoader(true);
    try {
      const response = await getTasksData();
      if (response?.success) {
        setTasks(response?.data?.tasks || []);
      }
    } catch (error) {
      console.error("Error fetching tasks data:", error);
    } finally {
      setLoader(false);
    }
  };

  const handleGetWorkers = async () => {
    setLoader(true);
    try {
      const response = await getWorkersData();
      if (response?.success) {
        setWorkers(response?.data?.worker || []);
      }
    } catch (error) {
      console.error("Error fetching workers data:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    setLoader(true);
    const debounceTimer = setTimeout(() => {
      handleGetData(itemsPerPage, currentPage);
      handleGetTasks();
      handleGetWorkers();
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [itemsPerPage, currentPage]);

  const formik = useFormik({
    initialValues: {
      projectName: "",
      taskName: "",
      assignWorker: [],
    },
    validationSchema: Yup.object({
      projectName: Yup.number().required("Project name is required"),
      taskName: Yup.number().required("Task name is required"),
      assignWorker: Yup.array()
        .of(Yup.number().required("Worker ID is required"))
        .min(1, "At least one worker must be assigned")
        .required("Worker assignment is required"),
    }),

    onSubmit: (values) => {
      setIsLoading(true);
      const requestData = {
        workers: values?.assignWorker,
        project_id: values.projectName,
        task_id: values?.taskName,
      };
      AllocateProject(requestData)
        .then((response) => {
          CustomToast({
            type: "success",
            message: `Project Allocated Succesfully`,
          });
          setIsLoading(false);
          navigate("/view-project");
        })
        .catch((error) => {
          CustomToast({
            type: "error",
            message: `${error}`,
          });
          setIsLoading(false);
        });
    },
  });

  const handleSelectChange = (field, value) => {
    formik.setFieldValue(field, value);
  };

  return (
    <div className="w-full flex flex-col gap-6 pt-8 pb-40">
      <ListHeader
        mainHeading="Project"
        placeholder="Search"
        btnShow={true}
        linkBreadCrum="/view-project"
        blinkBreadCrumText="Project"
        blinkBreadCrumText1="Worker Project Allocation"
        searchShow={false}
        showLikeFilter={false}
        DropDown={false}
      />
      <div className="px-6 pt-3 bg-white border border-[#DDDDDD] rounded-[8px]">
        <h2 className="text-[20px] font-semibold text-[#1E3A8A] mb-4">
          Worker Project Allocation
        </h2>
        <form onSubmit={formik.handleSubmit}>
          <div className="mb-4">
            <Select
              placeholder="Project name"
              className="w-full h-[52px] border border-[#E9E9E9] rounded-[4px] px-2"
              suffixIcon={DropDown}
              onChange={(value) => handleSelectChange("projectName", value)}
              onBlur={() => formik.setFieldTouched("projectName")}
            >
              {projectData?.map((project) => (
                <Option key={project?.id} value={project?.id}>
                  {project?.title}
                </Option>
              ))}
            </Select>
            {formik.touched.projectName && formik.errors.projectName && (
              <p className="text-red-500 text-sm mt-1">
                {formik.errors.projectName}
              </p>
            )}
          </div>
          <div className="grid md:grid-cols-2 grid-cols-1 gap-4 mb-4">
            <div>
              <Select
                placeholder="Task name"
                className="w-full h-[52px] border border-[#E9E9E9] rounded-[4px] px-2"
                suffixIcon={DropDown}
                onChange={(value) => handleSelectChange("taskName", value)}
                onBlur={() => formik.setFieldTouched("taskName")}
              >
                {tasks?.map((task) => (
                  <Option key={task?.id} value={task?.id}>
                    {task?.title}
                  </Option>
                ))}
              </Select>
              {formik.touched.taskName && formik.errors.taskName && (
                <p className="text-red-500 text-sm mt-1">
                  {formik.errors.taskName}
                </p>
              )}
            </div>
            <div>
              <Select
                placeholder="Assign workers"
                mode="multiple"
                className={`w-full min-h-[52px] border border-[#E9E9E9] rounded-[4px] pt-[10px] pl-2 pr-4 cursor-pointer`}
                suffixIcon={DropDown}
                onChange={(value) => handleSelectChange("assignWorker", value)}
                onBlur={() => formik.setFieldTouched("assignWorker")}
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {workers?.map((worker) => (
                  <Option key={worker?.id} value={worker?.id}>
                    {`${worker?.username} (${worker?.experience} years of experience, expertise in ${worker?.expertise})`}
                  </Option>
                ))}
              </Select>

              {formik.touched.assignWorker && formik.errors.assignWorker && (
                <p className="text-red-500 text-sm mt-1">
                  {formik.errors.assignWorker}
                </p>
              )}
            </div>
          </div>
        </form>
      </div>
      <button
        onClick={formik.handleSubmit}
        type="submit"
        className="w-[80px] mt-1 mb-[2rem] h-[44px] bg-[#F97316] text-white font-medium text-[20px] rounded-[4px] hover:bg-orange-600"
      >
        {isLoading ? <ButtonLoader /> : <p>Save</p>}
      </button>
    </div>
  );
};

export default WorkerProjectAllocation;
